import React, { useEffect, useState, useRef } from 'react';
import * as Icon from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../core/data/redux/store';
import { Toast } from 'primereact/toast';
import { getAllContactRequests, deleteContactRequest, updateContactRequest } from '../../../services/contactRequestServices';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import TableNotFound from '../../../app/common/components/TableNotFound';
import { formatDate } from '../../../utils/formatDate';
import { Dialog } from 'primereact/dialog';

const ContactRequests = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useRef<Toast>(null);
  const {
    contactRequests,
    isLoading,
    isError,
    errorMessage,
    contactRequestCreated,
    contactRequestUpdated,
    unreadCount
  } = useSelector((state: any) => ({
    contactRequests: state.contactRequest.contactRequests,
    isLoading: state.contactRequest.isLoading,
    isError: state.contactRequest.isError,
    errorMessage: state.contactRequest.errorMessage,
    contactRequestCreated: state.contactRequest.contactRequestCreated,
    contactRequestUpdated: state.contactRequest.contactRequestUpdated,
    unreadCount: state.contactRequest.unreadCount
  }));

  const [selectedContactRequest, setSelectedContactRequest] = useState<any>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllContactRequests());
    return () => {
      dispatch({ type: 'RESET_CONTACT_REQUEST_ERRORS' });
    };
  }, [dispatch, contactRequestCreated, contactRequestUpdated]);

  const handleDelete = (rowData: any) => {
    dispatch(deleteContactRequest(rowData.id));
  };

  const handleMarkAsRead = (rowData: any) => {
    dispatch(updateContactRequest(rowData.id, { ...rowData, is_read: true }));
  };

  const actionButton = (rowData: any) => (
    <div className="table-actions d-flex">
      <a
        className={`delete-table me-2 ${rowData.is_read ? 'disabled' : ''}`}
        href="#"
        onClick={e => {
          e.preventDefault();
          if (!rowData.is_read) {
            handleMarkAsRead(rowData);
          }
        }}
        style={{ 
          opacity: rowData.is_read ? 0.5 : 1,
          pointerEvents: rowData.is_read ? 'none' : 'auto',
          cursor: rowData.is_read ? 'not-allowed' : 'pointer'
        }}
      >
        <Icon.Eye className={`react-feather-custom ${rowData.is_read ? 'text-muted' : ''}`} />
      </a>
      <a
        onClick={e => {
          e.preventDefault();
          handleDelete(rowData);
        }}
        className="delete-table border-none"
        href="#"
      >
        <Icon.Trash2 className="react-feather-custom" />
      </a>
    </div>
  );

  const nameTemplate = (rowData: any) => (
    <a
      href="#"
      onClick={(e) => {
        e.preventDefault();
        setSelectedContactRequest(rowData);
        setIsDetailModalOpen(true);
      }}
      className="text-primary"
      style={{ textDecoration: 'none' }}
    >
      {rowData.full_name}
    </a>
  );

  return (
    <>
      <Toast ref={toast} />
      <div className="page-wrapper page-settings" style={{ paddingTop: '0px' }}>
        <div className="content">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              gap: 20,
              alignItems: 'center',
              paddingBottom: '2rem',
              flexWrap: 'wrap',
            }}
          >
            <h5 style={{ margin: '0 0 2rem 0', padding: 0 }}>
              Contact Requests
              {unreadCount > 0 && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    backgroundColor: 'red',
                    borderRadius: '50%',
                    marginLeft: '8px',
                    verticalAlign: 'middle'
                  }}
                />
              )}
            </h5>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="table-resposnive">
                <DataTable
                  paginatorTemplate="RowsPerPageDropdown CurrentPageReport PrevPageLink PageLinks NextPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  value={contactRequests}
                  showGridlines
                  tableStyle={{ minWidth: '50rem' }}
                  loading={isLoading}
                  emptyMessage={
                    !isLoading && (
                      <TableNotFound
                        Icon={Icon.Mail}
                        title={'No Contact Requests Found'}
                      />
                    )
                  }
                >
                  <Column
                    header="#"
                    body={(rowData, options) => options.rowIndex + 1}
                    style={{ width: '3rem' }}
                  ></Column>
                  <Column 
                    sortable 
                    field="full_name" 
                    header="Name"
                    body={nameTemplate}
                  ></Column>
                  <Column sortable field="email" header="Email"></Column>
                  <Column sortable field="company_stage" header="Company Stage"></Column>
                  <Column sortable field="services" header="Services"></Column>
                  <Column
                    sortable
                    field="created_at"
                    header="Date"
                    body={rowData => formatDate(rowData.created_at)}
                  ></Column>
                  <Column
                    sortable
                    field="is_read"
                    header="Status"
                    body={rowData => (
                      <span className={`status-badge ${rowData.is_read ? 'read' : 'unread'}`}>
                        {rowData.is_read ? 'Read' : 'Unread'}
                      </span>
                    )}
                  ></Column>
                  <Column
                    field="action"
                    header="Actions"
                    body={actionButton}
                  ></Column>
                </DataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        visible={isDetailModalOpen}
        style={{ width: '50vw' }}
        onHide={() => setIsDetailModalOpen(false)}
        header="Contact Request Details"
      >
        {selectedContactRequest && (
          <div className="p-fluid">
            <div className="field">
              <label htmlFor="name">Name</label>
              <input className="form-control" id="name" value={selectedContactRequest.full_name} readOnly />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input className="form-control" id="email" value={selectedContactRequest.email} readOnly />
            </div>
            <div className="field">
              <label htmlFor="companyStage">Company Stage</label>
              <input className="form-control" id="companyStage" value={selectedContactRequest.company_stage} readOnly />
            </div>
            <div className="field">
              <label htmlFor="services">Services</label>
              <input className="form-control" id="services" value={selectedContactRequest.services} readOnly />
            </div>
            <div className="field">
              <label htmlFor="description">Description</label>
              <input className="form-control" id="description" value={selectedContactRequest.description} readOnly />
            </div>
            <div className="field">
              <label htmlFor="date">Date</label>
              <input
                className="form-control"
                id="date"
                value={formatDate(selectedContactRequest.created_at)}
                readOnly
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default ContactRequests; 